import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLAN_INFO } from '../config/tier-config';
import './landing-page.css';

// Kept for a future reinstatement — not deleted, just not rendered while the
// landing page leads with the trend+volume story instead of the full
// strategy list. See doc/landingpage/landing-page-merge-brief.md.
const SHOW_STRATEGY_CARDS = false;

const STRATEGIES = [
  { icon: '&#127993;', name: 'RBPullback',    desc: 'Price stretched from rising/falling 50 SMA with RSI confirmation.', tier: 'Pro' },
  { icon: '&#128225;', name: 'RSI-MTF',       desc: 'Multi-timeframe RSI alignment with 4H crossover trigger.',           tier: 'Pro' },
  { icon: '&#128202;', name: 'CCI Pullback',  desc: 'CCI excursion beyond ±150, pulling back into the signal zone.',       tier: 'Elite' },
  { icon: '&#128200;', name: 'MTF 50 SMA',    desc: 'Two timeframe SMAs aligned, price pulls back past the inner SMA.',    tier: 'Elite' },
  { icon: '&#127919;', name: 'Trend Pullback',desc: '9 EMA vs 20 SMA alignment — price touches the EMA for entry.',        tier: 'Elite' },
  { icon: '&#128021;', name: 'ProjectTD',     desc: 'Rising/falling 50 SMA + Stochastic filter + MACD signal direction.',  tier: 'Elite' },
];

// Landing page shows only Free + Basic for now — Pro/Elite still exist in the
// product (Settings, /pricing, alert routing all still use the full
// PLAN_INFO), this just trims what the marketing page markets today.
const LANDING_PLAN_KEYS = ['free', 'basic'];

const BENEFITS = [
  {
    icon: <path d="M4 16l5-6 4 3 7-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
    title: 'Trade with the trend, not against it',
    desc: 'Trend direction is read from 50 SMA and 9/20 EMA alignment across weekly, daily, and 4H candles, so a signal only fires while price is already moving that direction.',
  },
  {
    icon: <path d="M4 20V10M10 20V4M16 20v-7M22 20v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />,
    title: 'Filter out the fakeouts',
    desc: "Volume Spike confirmation checks traded volume against that coin's own rolling baseline, so a move only counts once it breaks out of its normal range, not a number fixed for every coin.",
  },
  {
    icon: <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
    title: 'Two confirmations, one alert',
    desc: 'When trend direction and a volume spike line up on the same coin in the same window, it becomes a Triple Alignment, not two separate maybes to weigh on your own.',
  },
  {
    icon: <>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    </>,
    title: 'An hour is the longest you wait',
    desc: 'All 100 tracked coins are rechecked every hour against live Binance OHLCV candles, so a shift in trend or a spike in volume reaches you within the hour it happened.',
  },
];

const PIPELINE = [
  { num: '01', title: 'Scan',    desc: 'Each of the 100 tracked coins is checked for trend direction across three timeframes.' },
  { num: '02', title: 'Confirm', desc: 'A candidate only advances once Volume Spike confirmation clears its rolling baseline for that coin.' },
  { num: '03', title: 'Alert',   desc: 'Confirmed signals land on your dashboard and inbox, with Telegram delivery and per-strategy cooldowns from Basic up.' },
];

const FAQ = [
  { q: 'Can I cancel anytime?',         a: 'Yes — cancel from your settings at any time. Your access continues until the end of the billing period.' },
  { q: 'What markets do you cover?',    a: 'Top 100 cryptocurrencies by market cap, with real-time data from CoinGecko and Binance.' },
  { q: 'How are signals generated?',    a: 'Signals are computed server-side from live Binance OHLCV candles across weekly, daily, and 4H timeframes — refreshed hourly.' },
  { q: 'What does volume confirmation actually check?', a: "A spike is measured against each coin's own rolling volume baseline, not a fixed threshold, so a low cap coin and a top 10 coin are each judged against their own normal trading activity." },
  { q: 'Do I get email alerts?',        a: 'Yes. All plans receive email alerts when a new signal fires, with configurable cooldowns to avoid noise.' },
  { q: 'Can I try before subscribing?', a: 'The Free plan gives you the live dashboard and alerts log — no credit card needed.' },
];

// Two-line statement between the hero and the benefits section — words fade
// in one at a time as it scrolls into view.
function TaglineReveal({ text }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const words = el.querySelectorAll('.tagline-word');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      words.forEach(w => w.classList.add('active'));
      return;
    }

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.6, rootMargin: '0px 0px -35% 0px' });

    words.forEach(w => io.observe(w));
    return () => io.disconnect();
  }, [text]);

  return (
    <section className="tagline-section" aria-label="Core benefit">
      <p className="tagline-text" ref={containerRef}>
        {text.split(' ').map(function(word, i) {
          return <span key={i} className="tagline-word">{word}{' '}</span>;
        })}
      </p>
    </section>
  );
}

// Hero visual — illustrates the trend + volume combo the headline promises.
function SignalCard() {
  return (
    <div className="signal-card">
      <div className="signal-card-head">
        <div className="signal-pair">
          <span className="signal-dot" aria-hidden="true"></span>
          <span className="signal-pair-name">SOL / USDT</span>
        </div>
        <span className="signal-meta">4H &#183; 14:00 UTC</span>
      </div>
      <div className="signal-chart">
        <svg viewBox="0 0 460 150" width="100%" height="150" role="img" aria-label="Price trending up with a volume spike confirming the move on the final bar">
          <polyline points="0,110 40,102 80,108 120,90 160,94 200,72 240,78 280,55 320,60 360,38 400,42 440,20" fill="none" stroke="#48D6B4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <g fill="#7B84FF" opacity="0.55">
            <rect x="0" y="132" width="20" height="10" /><rect x="40" y="128" width="20" height="14" />
            <rect x="80" y="134" width="20" height="8" /><rect x="120" y="126" width="20" height="16" />
            <rect x="160" y="130" width="20" height="12" /><rect x="200" y="122" width="20" height="20" />
            <rect x="240" y="128" width="20" height="14" /><rect x="280" y="118" width="20" height="24" />
            <rect x="320" y="124" width="20" height="18" /><rect x="360" y="114" width="20" height="28" />
          </g>
          <rect x="400" y="90" width="20" height="52" fill="#48D6B4" />
          <rect x="440" y="60" width="20" height="82" fill="#48D6B4" />
        </svg>
      </div>
      <div className="signal-tags">
        <span className="signal-tag signal-tag-trend">Trend: rising 50 SMA</span>
        <span className="signal-tag signal-tag-volume">Volume: +212% of baseline</span>
        <span className="signal-tag signal-tag-confirm">Confirmed</span>
      </div>
    </div>
  );
}

function PricingCard({ planKey, plan, onSelect }) {
  return (
    <div className="pricing-card">
      <div className="pricing-plan-name">{plan.name}</div>
      <div className="pricing-price">{plan.label}</div>
      <ul className="pricing-features">
        {plan.features.map(function(f, i) {
          return <li key={i}><span className="check">&#10003;</span>{f}</li>;
        })}
      </ul>
      <button
        className="pricing-cta"
        onClick={function() { onSelect(planKey); }}
      >
        {planKey === 'free' ? 'Get started free' : 'Start ' + plan.name}
      </button>
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();

  function handlePlanSelect(planKey) {
    if (planKey === 'free') {
      navigate('/login');
    } else {
      navigate('/login?plan=' + planKey);
    }
  }

  return (
    <div className="landing-page">

      {/* Nav */}
      <header className="landing-header">
        <div className="landing-logo">
          <img src="/logo.svg" alt="CryptoProScanner" className="landing-logo-img" />
        </div>
        <div className="landing-header-actions">
          <button className="header-signin-btn" onClick={function() { navigate('/login'); }}>Sign in</button>
          <button className="header-cta-btn"    onClick={function() { navigate('/login'); }}>Get started</button>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-pill">&#9889; Real-time crypto signals</div>
          <h1 className="hero-headline">
            Catch the trend.<br />
            <span className="hero-highlight">Confirm it with volume.</span>
          </h1>
          <p className="hero-sub">
            CryptoProScanner scans the top 100 coins for early trend direction, then requires
            a volume spike before it calls a signal confirmed, so the fakeouts get filtered
            before they reach your inbox.
          </p>
          <div className="hero-actions">
            <button className="hero-cta-btn" onClick={function() { navigate('/login'); }}>
              Start free &#8594;
            </button>
            <button className="hero-secondary-btn" onClick={function() {
              document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' });
            }}>
              See pricing
            </button>
          </div>
          <p className="hero-note">Free plan available &#183; No credit card required</p>
        </div>

        <div className="hero-visual">
          <SignalCard />
          <div className="hero-stats-grid hero-stats-grid-3">
            <div className="hero-stat"><span className="hero-stat-n">100</span><span className="hero-stat-l">Coins tracked</span></div>
            <div className="hero-stat"><span className="hero-stat-n">W&#183;D&#183;4H</span><span className="hero-stat-l">Timeframes</span></div>
            <div className="hero-stat"><span className="hero-stat-n">1h</span><span className="hero-stat-l">Refresh cycle</span></div>
          </div>
        </div>
      </section>

      {/* Tagline reveal */}
      <TaglineReveal text="Trend tells you which way is moving. Volume tells you if the move is real." />

      {/* Benefits */}
      <section className="benefits-section">
        <h2 className="section-title">Direction alone is not a signal</h2>
        <p className="section-sub">Trend and volume answer different questions. CryptoProScanner only calls a move confirmed when both agree.</p>
        <div className="benefits-grid">
          {BENEFITS.map(function(b, i) {
            return (
              <div key={i} className="benefit-card">
                <div className="benefit-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24">{b.icon}</svg>
                </div>
                <div className="benefit-title">{b.title}</div>
                <div className="benefit-desc">{b.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="pipeline-section">
        <h2 className="section-title">Scan, confirm, alert</h2>
        <p className="section-sub">The same pipeline runs for every coin, every hour.</p>
        <div className="pipeline-grid">
          {PIPELINE.map(function(step, i) {
            return (
              <div key={i} className="pipeline-step">
                <div className="pipeline-num">{step.num}</div>
                <div className="pipeline-title">{step.title}</div>
                <div className="pipeline-desc">{step.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Strategies — kept for a future reinstatement, not rendered for now */}
      {SHOW_STRATEGY_CARDS && (
        <section className="strategies-section">
          <h2 className="section-title">6 battle-tested strategies</h2>
          <p className="section-sub">Each strategy is pre-computed server-side and paired with email alerts.</p>
          <div className="strategies-grid">
            {STRATEGIES.map(function(s, i) {
              return (
                <div key={i} className="strategy-card">
                  <div className="strategy-card-header">
                    <span className="strategy-icon" dangerouslySetInnerHTML={{ __html: s.icon }} />
                    <span className={'strategy-tier strategy-tier-' + s.tier.toLowerCase()}>{s.tier}</span>
                  </div>
                  <div className="strategy-name">{s.name}</div>
                  <div className="strategy-desc">{s.desc}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Pricing — Free + Basic only for now; Pro/Elite still exist in the product */}
      <section className="pricing-section" id="pricing">
        <h2 className="section-title">Simple, transparent pricing</h2>
        <p className="section-sub">Upgrade or cancel anytime.</p>
        <div className={'pricing-cards' + (LANDING_PLAN_KEYS.length <= 2 ? ' pricing-cards-compact' : '')}>
          {Object.entries(PLAN_INFO)
            .filter(function(entry) { return LANDING_PLAN_KEYS.includes(entry[0]); })
            .map(function(entry) {
              return <PricingCard key={entry[0]} planKey={entry[0]} plan={entry[1]} onSelect={handlePlanSelect} />;
            })}
        </div>
        <p className="pricing-note">Pro and Elite plans are coming soon.</p>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2 className="section-title">Frequently asked questions</h2>
        <div className="faq-list">
          {FAQ.map(function(item, i) {
            return (
              <div key={i} className="faq-item">
                <div className="faq-q">{item.q}</div>
                <div className="faq-a">{item.a}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="footer-cta-section">
        <h2>Ready to trade with confirmation, not guesswork?</h2>
        <button className="hero-cta-btn" onClick={function() { navigate('/login'); }}>
          Create your free account &#8594;
        </button>
      </section>

      <footer className="landing-footer">
        <span>&#169; 2025 CryptoProScanner</span>
        <button className="landing-footer-link" onClick={() => navigate('/support')}>Support &amp; FAQ</button>
        <span className="landing-footer-link-disabled" title="Not published yet">Privacy policy</span>
        <span className="landing-footer-link-disabled" title="Not published yet">Terms</span>
      </footer>

    </div>
  );
}

export default LandingPage;
